import { Button, Grid, Group, Stack, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconDownload, IconRestore } from '@tabler/icons-react';
import {
  useRevenueScenario,
  useScenarioExport,
  type MonumentTerms,
  type RevenueOverview,
  type ScenarioPatch,
} from 'src/secret-menu/billing/hooks/useRevenue';
import ModellerControls from './ModellerControls';
import ModellerResults from './ModellerResults';
import { changedAssumptions } from './scenario';

type Props = {
  overview: RevenueOverview;
  terms: MonumentTerms;
  includeInternal: boolean;
  /** Only what the scenario changes; empty is the current state. */
  patch: ScenarioPatch;
  onPatchChange: (patch: ScenarioPatch) => void;
};

/**
 * Assumptions in, revenue out. The back end prices every scenario with the same rules as the
 * Revenue tab, so with nothing changed the figures are exactly today's.
 */
export default function RevenueModeller({
  overview,
  terms,
  includeInternal,
  patch,
  onPatchChange,
}: Props) {
  const currentState = overview.today;
  // Typing in a box changes the patch on every keystroke; the back end prices it once typing stops.
  const [debounced] = useDebouncedValue(patch, 300);
  const somethingSet = Object.keys(debounced).length > 0;
  const { data: modelled } = useRevenueScenario(debounced, includeInternal, somethingSet);
  const result = somethingSet ? (modelled ?? currentState) : currentState;
  const changes = changedAssumptions(patch, somethingSet ? result.overrides.length : 0);
  const isCurrentState = changes.length === 0;
  // While Monument is at full adoption, its controls measure a change against that, not today.
  const monumentBaseline = patch.monumentFullAdoption
    ? overview.fullAdoption.monument
    : currentState.monument;
  const { exportScenario, isExporting } = useScenarioExport();

  const download = () =>
    exportScenario({ patch, includeInternal }).catch(() =>
      notifications.show({ color: 'red', message: 'Could not export the revenue scenario' }),
    );

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center" wrap="wrap">
        <Group gap="sm">
          <Button
            leftSection={<IconRestore size={16} />}
            variant={isCurrentState ? 'filled' : 'light'}
            onClick={() => onPatchChange({})}
          >
            Current state
          </Button>
          <Text size="textSm" c={isCurrentState ? 'dimmed' : 'orange'}>
            {isCurrentState ? 'Showing the current state' : `Changed: ${changes.join(', ')}`}
          </Text>
        </Group>
        <Button
          variant="light"
          leftSection={<IconDownload size={16} />}
          loading={isExporting}
          onClick={download}
        >
          Export to Excel
        </Button>
      </Group>

      <Text size="textXs" c="dimmed" mt={-8}>
        For modelling only. Nothing here changes billing.
      </Text>

      <Grid gap="md">
        <Grid.Col span={{ base: 12, md: 5, lg: 4 }}>
          <ModellerControls
            patch={patch}
            result={result}
            currentState={currentState}
            monumentBaseline={monumentBaseline}
            firmsChanged={somethingSet ? result.overrides.length : 0}
            terms={terms}
            pipelineFirms={overview.pipeline.length}
            onChange={onPatchChange}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 7, lg: 8 }}>
          <ModellerResults
            result={result}
            currentState={currentState}
            isCurrentState={isCurrentState}
          />
        </Grid.Col>
      </Grid>
    </Stack>
  );
}
